/**
 * Validation Engine — Builds Zod schemas from field definitions.
 *
 * Pure function. Uses `t()` for i18n-aware error messages (I18N-RULE-004).
 * Date validation uses z.string().regex() — NEVER z.date() (project-context §3).
 *
 * @module validationEngine
 * @see FMPV-MVWC-E001-S004
 */

import { z } from 'zod';
import type { MasterPatternViewFieldDefinition } from '../types';

/** ISO date-only pattern: YYYY-MM-DD */
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Builds a Zod validation schema from an array of field definitions.
 *
 * Only fields with `showInForm: true` are included in the schema.
 * Error messages use the provided `t()` function for i18n.
 *
 * @param fields - Field definitions to build the schema from
 * @param t - Translation function for error messages
 * @returns A Zod object schema keyed by fieldName
 */
export function buildZodSchema(
  fields: MasterPatternViewFieldDefinition[],
  t: (key: string) => string
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    if (field.showInForm === false) continue;

    shape[field.fieldName] = buildFieldSchema(field, t);
  }

  return z.object(shape) as z.ZodObject<Record<string, z.ZodTypeAny>>;
}

/**
 * Builds a Zod schema for a single field.
 * Maps inputType/dataType to the appropriate Zod chain.
 */
function buildFieldSchema(
  field: MasterPatternViewFieldDefinition,
  t: (key: string) => string
): z.ZodTypeAny {
  const inputType = field.inputType ?? inferInputTypeFromDataType(field.dataType);

  switch (inputType) {
    case 'number':
    case 'decimal':
      return buildNumberSchema(field, t);
    case 'boolean':
      return buildBooleanSchema();
    case 'date':
      return buildDateSchema(field, t);
    case 'select':
      // Select options can hold string OR number values — delegate to dataType.
      if (field.dataType === 'number' || field.dataType === 'decimal') {
        return buildNumberSchema(field, t);
      }
      return buildStringSchema(field, t);
    case 'text':
    case 'textarea':
    case 'lookup':
    default:
      return buildStringSchema(field, t);
  }
}

function buildStringSchema(
  field: MasterPatternViewFieldDefinition,
  t: (key: string) => string
): z.ZodTypeAny {
  const requiredMsg = t('validation.required');

  if (field.isRequired) {
    // z.preprocess coerces undefined/null → '' so min(1) fires the custom
    // message instead of Zod v4's default "Invalid input" type error.
    let inner = z.string().min(1, { message: requiredMsg });
    if (field.maxLength !== undefined) {
      inner = inner.max(field.maxLength, { message: t('validation.maxLength') });
    }
    return z.preprocess((val) => (val === undefined || val === null ? '' : val), inner);
  }

  // Non-required: coerce null/undefined to '' so z.string() accepts them
  // without Zod v4's default "Invalid input" type error on null values from API responses.
  let inner = z.string();
  if (field.maxLength !== undefined) {
    inner = inner.max(field.maxLength, { message: t('validation.maxLength') });
  }
  return z.preprocess((val) => (val === undefined || val === null ? '' : val), inner);
}

function buildNumberSchema(
  field: MasterPatternViewFieldDefinition,
  t: (key: string) => string
): z.ZodTypeAny {
  const schema = z.number({ message: t('validation.number') });

  if (field.isRequired) {
    return schema;
  }

  return schema.optional() as unknown as z.ZodTypeAny;
}

function buildBooleanSchema(): z.ZodTypeAny {
  return z.boolean().optional() as unknown as z.ZodTypeAny;
}

function buildDateSchema(
  field: MasterPatternViewFieldDefinition,
  t: (key: string) => string
): z.ZodTypeAny {
  let schema = z.string().regex(DATE_REGEX, { message: t('validation.dateFormat') });

  if (field.isRequired) {
    schema = schema.min(1, { message: t('validation.required') });
  }

  if (field.isRequired) {
    return schema;
  }

  return schema.optional() as unknown as z.ZodTypeAny;
}

function inferInputTypeFromDataType(dataType: string): string {
  const map: Record<string, string> = {
    string: 'text',
    number: 'number',
    decimal: 'decimal',
    boolean: 'boolean',
    date: 'date',
    guid: 'text',
  };
  return map[dataType] ?? 'text';
}
